/**
 * Pure transforms shared by the chart, the table, and CSV export.
 *
 * Kept React-free and side-effect-free so the figure-handling rules
 * (decimal-string in, decimal-string out; floats only for pixel geometry) are
 * unit-testable in isolation.
 */
import { formatDecimal, parseDecimalString } from "@compliance/domain";
import {
  type CorrectionDisplayMode,
  type SeriesOverlay,
  applyApprovedOverlay,
} from "./correctionOverlay";

/**
 * One plotted/tabulated point. `day` is the x-axis bucket key — a gas-day
 * (`YYYY-MM-DD`) for daily series or a bucket-start ISO timestamp for hourly;
 * either sorts lexicographically. `value: null` marks a gap. `n_readings` is the
 * bucket's sample count (hourly totalizer roles), a data-completeness signal.
 */
export type SeriesDatum = { day: string; value: string | null; n_readings?: number | null };

/** Line or bar — the chart's rendering mode, carried in the URL. */
export type ChartVariant = "line" | "bar";

/**
 * Read the chart mode from a URL parameter.
 *
 * Chart mode is URL state for the same reason sources, range, granularity and
 * correction mode are: reloading or sharing a link has to reproduce the view.
 * It was component state until a browser-test review noticed that a reload
 * silently reverted Bar to Line.
 *
 * Anything unrecognised — absent, empty, a hand-edited link — falls back to
 * `line`, so a bad parameter renders the default chart rather than nothing.
 */
export function parseChartVariant(raw: string | null): ChartVariant {
  return raw === "bar" ? "bar" : "line";
}

export type ChartSeriesInput = {
  /** Unique, stable key (role::facility). */
  id: string;
  label: string;
  unit: string;
  /** Mantine color reference, e.g. "indigo.6". */
  color: string;
  points: SeriesDatum[];
  /** Server-authoritative period total (decimal string), if known. */
  total?: { value: string; basis: string } | null;
};

/**
 * Map `GET /data/series` points onto the chart datum. The x-key is the Pacific
 * gas-day (`YYYY-MM-DD`, sliced from the bucket-start) for daily, or the full
 * bucket-start ISO for hourly — both sort lexicographically into time order.
 */
export function toSeriesData(
  points: { bucket_start: string; value: string; n_readings?: number | null }[],
  granularity: string,
): SeriesDatum[] {
  return points.map((p) => ({
    day: granularity === "hourly" ? p.bucket_start : p.bucket_start.slice(0, 10),
    value: p.value,
    n_readings: p.n_readings ?? null,
  }));
}

/**
 * Display label for a bucket x-key: the gas-day string as-is for daily, or a
 * compact Pacific "Mon D, HH:MM" for an hourly bucket-start ISO timestamp.
 */
export function formatBucketLabel(key: string, granularity: string): string {
  if (granularity !== "hourly") return key;
  const d = new Date(key);
  return Number.isNaN(d.getTime())
    ? key
    : new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(d);
}

/** Sorted union of every day present across the given series. */
export function unionDays(series: { points: SeriesDatum[] }[]): string[] {
  const set = new Set<string>();
  for (const s of series) for (const p of s.points) set.add(p.day);
  return [...set].sort();
}

/**
 * Convert a canonical decimal string to a number for chart geometry only.
 * Returns `null` on a gap or an unparseable value (chart renders a gap rather
 * than throwing in a render path). This is the ONLY place a figure becomes a
 * float, and the result never round-trips back to the backend.
 */
export function toPlotNumber(value: string | null | undefined): number | null {
  if (value == null) return null;
  try {
    return parseDecimalString(value).toNumber();
  } catch {
    // Upload-row values aren't compliance figures and may not be canonical;
    // fall back to a tolerant parse so they still plot.
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}

/** Correction-overlay context for the export, keyed by `ChartSeriesInput.id`. */
export type CsvCorrections = {
  mode: CorrectionDisplayMode;
  overlays: Map<string, SeriesOverlay>;
};

/**
 * CSV export: one column per series, values as the RAW decimal strings
 * (byte-faithful — no float round-trip), a TOTAL row from each series'
 * server total. Empty cells are blank (a gap), not "0".
 *
 * With `corrections` in a non-"db" mode, each column exports the APPROVED
 * effective values (substituted cells carry the raw `after_value`, excluded
 * cells go blank) — pending proposals are never exported in any mode. The
 * TOTAL stays the server figure (the overlay never recomputes), relabeled
 * "TOTAL (server, pre-correction)" so the artifact self-documents that the
 * total is not the column sum.
 */
export function buildCsv(
  series: ChartSeriesInput[],
  days: string[],
  corrections?: CsvCorrections,
): string {
  const header = ["day", ...series.map((s) => csvCell(`${s.label} (${s.unit})`))];
  const overlayFor = (s: ChartSeriesInput) =>
    corrections?.mode !== undefined && corrections.mode !== "db"
      ? corrections.overlays.get(s.id)
      : undefined;
  const valueByDay = series.map((s) => {
    const points = corrections
      ? applyApprovedOverlay(s.points, corrections.overlays.get(s.id), corrections.mode)
      : s.points;
    const m = new Map<string, string>();
    for (const p of points) if (p.value != null) m.set(p.day, p.value);
    return m;
  });
  const anyApplied = series.some((s) => {
    const overlay = overlayFor(s);
    if (!overlay) return false;
    for (const cell of overlay.cells.values()) if (cell.approved != null) return true;
    return false;
  });

  const lines = [header.join(",")];
  for (const day of days) {
    const cells = [day, ...valueByDay.map((m) => m.get(day) ?? "")];
    lines.push(cells.join(","));
  }
  const totalLabel = anyApplied ? csvCell("TOTAL (server, pre-correction)") : "TOTAL";
  const totalCells = [totalLabel, ...series.map((s) => s.total?.value ?? "")];
  lines.push(totalCells.join(","));
  return lines.join("\r\n");
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Display formatter for a decimal-string cell; returns "—" for a gap. Pass
 * `decimalPlaces` to round for display (the Data tab rounds to 2); omit it to
 * preserve the figure's full precision.
 */
export function formatCell(value: string | null | undefined, decimalPlaces?: number): string {
  if (value == null) return "—";
  try {
    return formatDecimal(parseDecimalString(value), { decimalPlaces });
  } catch {
    return value;
  }
}
